import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import RegisterIcon from "@mui/icons-material/AppRegistration";
import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";
import {Link, useNavigate} from "react-router-dom";
import {Form, useForm} from "react-hook-form"
import React, {useState} from "react";
import axios from "axios";
import {Alert} from "@mui/material";
import Cookies from "js-cookie";
import {
    Email,
    LockReset,
    PublishTwoTone
} from "@mui/icons-material";

function encodeCredentials(username, password) {
    try {
        return btoa(`${username}:${password}`);
    } catch (error) {
        return "";
    }
}

export default function ResetPass() {
    const [alertSeverity, setAlertSeverity] = useState("info");
    const [alertMsg, setAlertMsg] = useState("Please enter your email to get a reset code ( valid for 24hrs ).");
    const {control} = useForm()

    // State variables
    const [emailAddress, setEmailAddress] = useState("");
    const [token, setToken] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [rePassword, setRePassword] = useState("");

    const navigate = useNavigate();

    // --- IMPLEMENTED: Actual Login Logic from Login View ---
    const loginUser = async () => {
        setAlertMsg("Password updated. Authenticating...");

        // 1. Encode the email and the NEW password
        const encodedCredentials = encodeCredentials(emailAddress, newPassword);

        // 2. Prepare data for Login API
        const formData = new URLSearchParams();
        formData.append('token', encodedCredentials);

        try {
            // 3. Call the Login API
            const response = await axios.post('../api/user/login', formData, {
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded'
                }
            });

            // 4. Set Session Cookies (Logic from Login.js)
            setAlertMsg("User authenticated, authorizing access...");
            setAlertSeverity("success");

            const expirationTime = new Date(new Date().getTime() + (60000 * 60 * 168)); // 1 week
            const passHash = response.data.passHash;

            Cookies.set('auth', passHash, { expires: expirationTime });
            Cookies.set('email', emailAddress, { expires: expirationTime });
            Cookies.set('woven_session', 'post-login', { expires: expirationTime });
            Cookies.set("analytics_section", "offers");

            // 5. Navigate to Home
            navigate("/");

        } catch (error) {
            // If the reset worked but auto-login failed
            console.error("Auto-login failed:", error);
            setAlertSeverity("warning");
            setAlertMsg("Password was reset, but auto-login failed. Please log in manually.");
            // Give them a moment to read the message, then send to login
            setTimeout(() => navigate("/login"), 3000);
        }
    };
    // -----------------------------------------------------------

    // --- FUNCTION: Handles the Request Code Button Click ---
    const handleRequestCode = async () => {
        if(!emailAddress) {
            setAlertSeverity("warning");
            setAlertMsg("Please enter your email address first.");
            return;
        }

        const resetToken = encodeCredentials(emailAddress, 'reset');
        const formData = new URLSearchParams();
        formData.append('token', resetToken);

        try {
            await axios.post('../api/user/reqPassRst', formData, {
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
            });

            setAlertSeverity("success");
            setAlertMsg("Reset code has been sent ( valid 24hrs ).");

        } catch (error) {
            setAlertSeverity("error");
            setAlertMsg("Email is not registered or connection failed.");
            console.error(error);
        }
    };

    // --- FUNCTION: Handles the Password Reset Submission ---
    const handleSubmit = async () => {
        if(newPassword !== rePassword) {
            setAlertSeverity("warning");
            setAlertMsg("New passwords do not match.");
            return;
        }

        const newToken = encodeCredentials(emailAddress, newPassword);

        const formData = new URLSearchParams();
        formData.append('token', token); // The reset code
        formData.append('new_token', newToken);

        try {
            const response = await axios.post('../api/user/resetPass', formData, {
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded'
                }
            });

            if (response.status === 200) {
                if (response.data === "Password was updated.") {
                    // Success: Call the implemented login method
                    await loginUser();
                } else {
                    setAlertSeverity("warning");
                    setAlertMsg(response.data);
                }
            }
        } catch (error) {
            setAlertSeverity("error");
            const errorMsg = error.response && error.response.data ? error.response.data : "The password reset failed.";
            setAlertMsg(errorMsg);
        }
    };

    return (
        <div className="Section">
            <div className="SectionLtr">
              <span className='SectionBox'>
                  <span className="SectionHeadingIcon"><LockReset sx={{fontSize: 43}}/></span>
                      <span className="SectionHeading">RESET</span>
                      <span className="SectionSubHeading">PASSWORD</span>
                  <div className="_div_line_dual_grad"></div>

            <Form onSubmit={handleSubmit}
                  control={control}>
                  <div className="_inputSection">
                      <div className="Alert">
                          <Alert severity={alertSeverity} className="Alert">{alertMsg}</Alert>
                      </div>

                      <div className="InputSection">
                          <div className="_inputLine">
                              <TextField
                                  label="Email address" size="small"
                                  type="text" required
                                  onChange={e => setEmailAddress(e.target.value)}
                              />
                          </div>
                      </div>
                       <div className="_button_holder">
                           <RequestButton onClick={handleRequestCode}/>
                      </div>
                      <div className="_div_line_dual_grad"></div>
                      <div className="InputSection">
                          <div className="_inputLine">
                              <TextField
                                  label="Reset Code" size="small"
                                  type="text" required
                                  onChange={e => setToken(e.target.value)}
                              />
                          </div>
                          <div className="_inputLine">
                              <TextField  label="New password" size="small"
                                          type="password" required
                                          onChange={e => setNewPassword(e.target.value)}/>
                          </div>
                          <div className="_inputLine">
                              <TextField label="Confirm new password" size="small"
                                         type="password" required
                                         onChange={e => setRePassword(e.target.value)}/>
                          </div>
                      </div>
                    <div className="_button_bar">
                      <span className="_button_holder">
                          <Link to="/login"><BackButton/></Link>
                      </span>
                       <span className="_button_holder">
                          <RegisterButton/>
                      </span>
                    </div>
                </div>
            </Form>
              </span>
            </div>
        </div>
    );
}

function RegisterButton() {
    return (
        <Button type="submit" variant="contained" startIcon={< PublishTwoTone/>}>
            Submit
        </Button>
    );
}

function BackButton() {
    return (
        <Button type="button" variant="outlined" startIcon={<ArrowBackOutlinedIcon/>}>
            LOGIN
        </Button>
    );
}

function RequestButton({ onClick }) {
    return (
        <Button
            type="button"
            variant="contained"
            color="primary"
            startIcon={<Email/>}
            onClick={onClick}
        >
            REQUEST CODE
        </Button>
    );
}